import { Star } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface StarProps {
  value: number;
  currentRating: number;
  onRatingChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function StarButton({
  value,
  currentRating,
  onRatingChange,
  size = 'md',
  className,
}: StarProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  const isFilled = value <= currentRating;

  return (
    <button
      type="button"
      onClick={() => onRatingChange(value)}
      className={cn(
        'active:scale-95 transition-transform duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 rounded',
        className
      )}
      aria-label={`Rate ${value} stars`}
    >
      <Star
        className={cn(
          sizeClasses[size],
          isFilled ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
        )}
      />
    </button>
  );
}

interface StarDisplayProps {
  rating: number;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showValue?: boolean;
}

export function StarDisplay({
  rating,
  maxRating = 5,
  size = 'md',
  className,
  showValue = false,
}: StarDisplayProps) {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className={cn('flex items-center gap-4', className)}>
      <div className="flex">
        {Array.from({ length: maxRating }, (_, i) => i + 1).map((star) => (
          <Star
            key={star}
            className={cn(
              sizeClasses[size],
              star <= Math.round(rating)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            )}
          />
        ))}
      </div>
      {showValue && (
        <span className="font-medium text-sm">
          {rating}/{maxRating}
        </span>
      )}
    </div>
  );
}

interface InteractiveStarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}

function InteractiveStarRating({
  rating,
  onRatingChange,
  maxRating = 5,
  size = 'md',
  className,
  disabled = false,
}: InteractiveStarRatingProps) {
  return (
    <div className={cn('flex gap-1', className)}>
      {Array.from({ length: maxRating }, (_, i) => i + 1).map((star) => (
        <StarButton
          key={star}
          value={star}
          currentRating={rating}
          onRatingChange={disabled ? () => {} : onRatingChange}
          size={size}
          className={disabled ? 'cursor-not-allowed opacity-50' : ''}
        />
      ))}
    </div>
  );
}

interface StarRatingProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  label?: string;
  description?: string;
  maxRating?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
  showValue?: boolean;
}

export function StarRating({
  rating,
  onRatingChange,
  label,
  description,
  maxRating = 5,
  size = 'md',
  className,
  disabled = false,
  showValue = false,
}: StarRatingProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {label && <Label className="text-sm font-medium">{label}</Label>}

      <InteractiveStarRating
        rating={rating}
        onRatingChange={onRatingChange}
        maxRating={maxRating}
        size={size}
        disabled={disabled}
      />

      {showValue && (
        <div className="flex items-center gap-2">
          <StarDisplay
            rating={rating}
            maxRating={maxRating}
            size={size}
            showValue
          />
        </div>
      )}

      {description && (
        <p className="text-xs text-gray-600 mt-1">{description}</p>
      )}
    </div>
  );
}
